import {useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {Luggage} from 'lucide-react';
import {Badge} from '@chekinapp/ui';
import {usePath} from 'hooks/common';
import {PATHS} from 'routes/constants';
import {SeeAllLink} from 'components/SeeAllLink';
import {SectionHeading} from 'components/SectionHeading';
import {useLuggageBookings} from 'hooks/useLuggageBookings';
import {LuggageBookingsList} from './LuggageBookingsList';

const HOME_PREVIEW_LIMIT = 2;

type Props = {
  className?: string;
};

function LuggageStorageSection({className}: Props) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {bookings, activeBookings} = useLuggageBookings();

  const shownIds = useRef<Set<string>>(new Set());
  const homeBookings = useMemo(() => {
    activeBookings.forEach(booking => shownIds.current.add(booking.id));
    return bookings.filter(booking => shownIds.current.has(booking.id));
  }, [bookings, activeBookings]);

  if (homeBookings.length === 0) {
    return null;
  }

  const previews = homeBookings.slice(0, HOME_PREVIEW_LIMIT);
  const hasMore = homeBookings.length > previews.length;

  return (
    <section className={className}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <SectionHeading icon={<Luggage size={18} />}>
            {t('luggage_booking.section_title')}
          </SectionHeading>
          {homeBookings.length > 1 && (
            <Badge
              variant="secondary"
              shape="pill"
              className="h-5 min-w-5 px-1.5 font-bold tabular-nums text-[color:var(--chekin-color-brand-blue)]"
            >
              {homeBookings.length}
            </Badge>
          )}
        </div>
        {hasMore && <SeeAllLink onClick={() => navigate(getFullPath(PATHS.luggage))} />}
      </div>

      <LuggageBookingsList bookings={previews} />
    </section>
  );
}

export {LuggageStorageSection};
